import { useState, useEffect, useCallback } from 'react';
import api, { formatApiError } from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import SectionHelp from '@/components/help/SectionHelp';
import HelpTooltip from '@/components/help/HelpTooltip';
import { Flower2, AlertTriangle, Ticket, X, Check, Coins } from 'lucide-react';

// S133 — Spa: la agenda del día con lo que hay que saber ANTES de atender.
//
// Dos cosas mandan en esta pantalla:
//
//   1. Lo que el huésped declaró (embarazo, presión alta, una alergia) se ve
//      junto a su cita, no escondido en su ficha. Un terapeuta que tiene que
//      ir a buscarlo, no lo busca.
//   2. El bono. Hasta ahora el contador de sesiones nunca bajaba; aquí se
//      descuenta con un botón y se puede devolver si la cita se cancela.

function Avisos({ a }) {
  if (!a?.tiene_ficha) {
    return (
      <span className="text-sm text-amber-700">
        Sin ficha de bienestar — pregúntale antes de empezar
      </span>
    );
  }
  if (!a.requiere_atencion) {
    return <span className="text-sm text-slate-500">Sin condiciones declaradas</span>;
  }
  return (
    <span className="inline-flex items-center gap-1 text-sm text-rose-700 font-medium">
      <AlertTriangle className="w-4 h-4" /> {a.aviso}
    </span>
  );
}

export default function SpaDesk() {
  const [cargando, setCargando] = useState(true);
  const [hoy, setHoy] = useState(null);
  const [abierto, setAbierto] = useState(null);   // huésped en detalle
  const [borrador, setBorrador] = useState(null);

  const cargar = useCallback(async () => {
    try {
      const { data } = await api.get('/tenant/spa/hoy');
      setHoy(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setCargando(false);
    }
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  async function abrirHuesped(cita) {
    try {
      const { data } = await api.get(`/tenant/spa/huespedes/${cita.crm_lead_id}`);
      setAbierto({ ...data, cita });
      setBorrador({
        conditions: (data.bienestar.condiciones || []).join(', '),
        allergies: (data.bienestar.alergias || []).join(', '),
        preferences: (data.bienestar.preferencias || []).join(', '),
        notes: data.bienestar.notas || '',
      });
    } catch (e) { toast.error(formatApiError(e)); }
  }

  async function guardarBienestar() {
    try {
      const { data } = await api.put(
        `/tenant/spa/huespedes/${abierto.cita.crm_lead_id}/bienestar`, borrador);
      toast.success('Ficha de bienestar guardada.');
      setAbierto({ ...abierto, bienestar: data.bienestar });
      await cargar();
    } catch (e) { toast.error(formatApiError(e)); }
  }

  async function usarSesion(cita, membershipId) {
    try {
      const { data } = await api.post(
        `/tenant/spa/citas/${cita.appointment_id}/usar-sesion`,
        membershipId ? { membership_id: membershipId } : {});
      toast.success(data.aviso);
      await cargar();
      if (abierto) await abrirHuesped(cita);
    } catch (e) { toast.error(formatApiError(e)); }
  }

  async function devolverSesion(cita) {
    const motivo = window.prompt(
      'La sesión regresa al bono del huésped.\n\n¿Por qué se devuelve?');
    if (!motivo || motivo.trim().length < 3) {
      if (motivo !== null) toast.error('Escribe el motivo.');
      return;
    }
    try {
      const { data } = await api.post(
        `/tenant/spa/citas/${cita.appointment_id}/devolver-sesion`, { motivo });
      toast.success(data.aviso);
      await cargar();
      if (abierto) await abrirHuesped(cita);
    } catch (e) { toast.error(formatApiError(e)); }
  }

  // S136 — cuánto se le debe a cada terapeuta. Se pide aparte y solo cuando se
  // pide: si este reporte fallara, la pantalla del día —que es con la que se
  // trabaja— no puede quedarse en blanco por un reporte.
  const [comisiones, setComisiones] = useState(null);
  const [verComisiones, setVerComisiones] = useState(false);

  const cargarComisiones = async () => {
    setVerComisiones(true);
    try {
      const { data } = await api.get('/tenant/spa/comisiones');
      setComisiones(data);
    } catch (e) {
      toast.error(formatApiError(e));
      setVerComisiones(false);
    }
  };

  if (cargando) return <div className="p-6 text-slate-500">Cargando el spa…</div>;

  return (
    <div className="p-6 space-y-5 max-w-4xl">
      <div className="flex items-center gap-2">
        <Flower2 className="w-6 h-6 text-emerald-600" />
        <h1 className="text-2xl font-semibold">Spa</h1>
      </div>

      <SectionHelp
        module="spa"
        title="los bonos y la ficha de bienestar"
        description={'Aquí ves las citas de hoy con lo que cada huésped declaró, y '
                     + 'descuentas las sesiones de su bono conforme lo atiendes.'}
        whatItDoesNot={'no decide por ti si un tratamiento procede. Te avisa lo que el '
                       + 'huésped declaró y el terapeuta decide.'} />

      {/* ── Comisiones del equipo (S136) ─────────────────────── */}
      <div>
        {!verComisiones ? (
          <div className="space-y-1">
            <Button variant="outline" size="sm" onClick={cargarComisiones}>
              <Coins className="w-4 h-4 mr-1" />Ver comisiones del equipo
            </Button>
            {/* La ayuda va VISIBLE y no dentro de un tooltip: el arnés de
                render no monta el tooltip, así que un botón envuelto en uno
                queda sin comprobar — y además así se lee sin tener que
                descubrir que hay que pasar el ratón por encima. */}
            <p className="text-[11px] text-slate-500">
              Cuánto se le debe a cada terapeuta por las sesiones que sí dio en
              los últimos 30 días.
            </p>
          </div>
        ) : (
          <div className="border rounded-lg p-3 space-y-2">
            <div className="flex items-start justify-between gap-2">
              <div>
                <p className="font-medium text-sm">Comisiones del equipo</p>
                {comisiones && (
                  <p className="text-xs text-slate-500">Del {comisiones.desde} al {comisiones.hasta}</p>
                )}
              </div>
              <Button variant="ghost" size="sm" onClick={() => setVerComisiones(false)}>
                <X className="w-4 h-4" />
              </Button>
            </div>
            {!comisiones && <p className="text-sm text-slate-500">Calculando…</p>}
            {comisiones && comisiones.por_terapeuta.length === 0 && (
              <p className="text-sm text-slate-600">No hay nada que comisionar en este periodo.</p>
            )}
            {comisiones && comisiones.por_terapeuta.map((f) => (
              <div key={f.terapeuta} className="flex items-center justify-between gap-2 border-b last:border-0 py-1.5 text-sm">
                <div>
                  <p>{f.terapeuta}</p>
                  <p className="text-xs text-slate-500">
                    {f.sesiones} {f.sesiones === 1 ? 'sesión' : 'sesiones'} · base ${f.base}
                  </p>
                </div>
                <span className="font-semibold">${f.comision}</span>
              </div>
            ))}
            {comisiones && comisiones.por_terapeuta.length > 0 && (
              <div className="flex justify-between font-semibold text-sm pt-1">
                <span>Total</span><span>${comisiones.total}</span>
              </div>
            )}
            {comisiones && comisiones.servicios_sin_comision.length > 0 && (
              <p className="text-xs text-amber-700 bg-amber-50 rounded p-2">
                Estos servicios <b>no</b> tienen comisión configurada y por eso no suman:
                {' '}{comisiones.servicios_sin_comision.join(', ')}. Se configura en
                Agenda → Servicios.
              </p>
            )}
            {comisiones && <p className="text-[11px] text-slate-500">{comisiones.ayuda}</p>}
          </div>
        )}
      </div>

      {/* Las dos reglas que cambian cómo se usa, siempre visibles. */}
      <div className="border-l-4 border-emerald-400 bg-emerald-50/50 pl-3 py-2 text-sm text-slate-700 space-y-1">
        <div>
          <b>Una cita descuenta una sesión.</b> Aunque le des dos veces al botón, el
          bono del huésped solo baja una vez. Si la cita se cancela, la sesión regresa.
        </div>
        <div>
          <b>La ficha avisa, no bloquea.</b> Si el huésped declaró algo, lo verás en
          rojo junto a su cita — pero quien decide si el tratamiento procede eres tú.
        </div>
      </div>

      {!hoy?.citas_de_hoy ? (
        <div className="border rounded-lg p-6 bg-slate-50 text-slate-700 space-y-2">
          <div className="font-medium">Hoy no hay citas agendadas.</div>
          <p className="text-sm">
            Las citas se agendan desde <b>Agenda</b>. En cuanto haya una, aquí verás
            al huésped, lo que declaró en su ficha y el bono del que se le descuenta
            la sesión.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-4 gap-3">
          <Tarjeta n={hoy.citas_de_hoy} t="Citas de hoy" />
          <Tarjeta n={hoy.con_aviso} t="Requieren atención" resaltar={hoy.con_aviso > 0} />
          <Tarjeta n={hoy.huespedes_sin_ficha} t="Sin ficha" />
          <Tarjeta n={hoy.sesiones_usadas_hoy} t="Sesiones usadas" />
        </div>
      )}

      <div className="space-y-2">
        {(hoy?.agenda || []).map((c) => (
          <div key={c.appointment_id} className="border rounded-lg p-3 bg-white">
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="font-medium">{c.huesped || 'Huésped'}</div>
                <div className="text-sm text-slate-500">
                  {c.tratamiento}
                  {c.hora && ` · ${new Date(c.hora).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`}
                </div>
                <Avisos a={{ tiene_ficha: c.tiene_ficha, requiere_atencion: c.requiere_atencion, aviso: c.aviso_de_bienestar }} />
              </div>
              <div className="text-right shrink-0 space-y-1">
                {c.sesion_descontada ? (
                  <>
                    <div className="text-sm text-emerald-700 inline-flex items-center gap-1">
                      <Check className="w-4 h-4" /> Sesión descontada
                    </div>
                    <div>
                      <Button size="sm" variant="ghost" onClick={() => devolverSesion(c)}>
                        Devolver
                      </Button>
                    </div>
                  </>
                ) : (
                  <Button size="sm" variant="outline" onClick={() => usarSesion(c)}>
                    <Ticket className="w-4 h-4 mr-1" /> Usar sesión del bono
                  </Button>
                )}
                <div>
                  <Button size="sm" variant="ghost" onClick={() => abrirHuesped(c)}>
                    Ver ficha
                  </Button>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {abierto && (
        <div className="fixed inset-0 bg-black/40 flex items-start justify-center p-6 overflow-y-auto z-50">
          <div className="bg-white rounded-lg p-5 w-full max-w-2xl space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="font-semibold">{abierto.cita.huesped || 'Huésped'}</h3>
              <Button variant="ghost" size="sm" onClick={() => setAbierto(null)}>
                <X className="w-4 h-4" />
              </Button>
            </div>

            <div>
              <h4 className="text-sm font-medium mb-1">Sus bonos</h4>
              {abierto.bonos.length === 0 && (
                <div className="text-sm text-slate-500">
                  No tiene bonos. Véndele uno desde el punto de venta o cóbrale
                  el tratamiento.
                </div>
              )}
              {abierto.bonos.map((b) => (
                <div key={b.id} className="flex items-center justify-between border rounded p-2 mb-1">
                  <div>
                    <span className="font-medium">{b.nombre}</span>
                    <div className="text-sm text-slate-500">
                      {b.sesiones_restantes ?? 0} sesión(es)
                      {b.vence && ` · vence ${new Date(b.vence).toLocaleDateString()}`}
                      {b.vencido && <span className="text-rose-700"> · vencido</span>}
                    </div>
                  </div>
                  {b.usable && !abierto.cita.sesion_descontada && (
                    <Button size="sm" variant="outline"
                            onClick={() => usarSesion(abierto.cita, b.id)}>
                      Usar de este
                    </Button>
                  )}
                </div>
              ))}
            </div>

            <div className="space-y-2">
              <h4 className="text-sm font-medium">
                Ficha de bienestar
                <HelpTooltip text="Lo que el huésped declara de su salud. No es un diagnóstico ni sustituye a un médico: sirve para que el terapeuta sepa qué evitar." />
              </h4>
              <div>
                <Input placeholder="Condiciones que declara (embarazo, presión alta…)"
                       value={borrador.conditions}
                       onChange={(e) => setBorrador({ ...borrador, conditions: e.target.value })} />
                <p className="text-xs text-slate-500 mt-1">
                  Sugeridas: {(abierto.condiciones_sugeridas || []).slice(0, 6).join(' · ')}
                </p>
              </div>
              <Input placeholder="Alergias (aceites, esencias…)"
                     value={borrador.allergies}
                     onChange={(e) => setBorrador({ ...borrador, allergies: e.target.value })} />
              <Input placeholder="Preferencias (presión suave, sin música…)"
                     value={borrador.preferences}
                     onChange={(e) => setBorrador({ ...borrador, preferences: e.target.value })} />
              <Textarea placeholder="Notas del terapeuta"
                        value={borrador.notes}
                        onChange={(e) => setBorrador({ ...borrador, notes: e.target.value })} />
              {abierto.bienestar.consentimiento_firmado && (
                <p className="text-xs text-emerald-700">
                  Consentimiento firmado por {abierto.bienestar.firmo}.
                </p>
              )}
              <Button onClick={guardarBienestar}>Guardar ficha</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Tarjeta({ n, t, resaltar }) {
  return (
    <div className={`border rounded-lg p-3 ${resaltar ? 'bg-rose-50 border-rose-200' : 'bg-white'}`}>
      <div className="text-2xl font-semibold tabular-nums">{n ?? 0}</div>
      <div className="text-sm text-slate-500">{t}</div>
    </div>
  );
}
